import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  Button,
  Grid,
  TextField,
  Divider,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  IconButton,
  Chip
} from '@mui/material';
import {
  Edit as EditIcon,
  PhotoCamera as PhotoCameraIcon,
  Save as SaveIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import axios from 'axios';
import config from '../../config';
import { compressImageIfNeeded, getCompressionInfo, getCompressionOptions } from '../../utils/imageCompression';

const Profile = () => {
  const { user, role, getToken } = useAuth();
  const { user: auth0User } = useAuth0();
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [profilePicture, setProfilePicture] = useState(null);
  const [profilePicturePreview, setProfilePicturePreview] = useState(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [compressing, setCompressing] = useState(false);
  const [compressionInfo, setCompressionInfo] = useState(null);

  useEffect(() => {
    // Initialize edited name with current user name
    setEditedName(user?.name || auth0User?.name || '');
    setProfilePicturePreview(user?.picture || auth0User?.picture || null);
  }, [user, auth0User]);

  const handleEditToggle = () => {
    if (editMode) {
      // Cancel edit - reset values
      setEditedName(user?.name || auth0User?.name || '');
      setProfilePicture(null);
      setProfilePicturePreview(user?.picture || auth0User?.picture || null);
    }
    setEditMode(!editMode);
  };

  const handleNameChange = (event) => {
    const value = event.target.value;
    // Basic validation - limit length and sanitize
    if (value.length <= 100) {
      setEditedName(value);
    }
  };

  const handleProfilePictureChange = async (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type first
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];

      if (!allowedTypes.includes(file.type)) {
        setMessage({
          type: 'error',
          text: 'Please select a valid image file (JPEG, PNG, or GIF)'
        });
        return;
      }

      try {
        setCompressing(true);
        setMessage(null);

        // Process the image (compress if needed)
        const compressionOptions = getCompressionOptions('profile');
        const processedFile = await compressImageIfNeeded(file, compressionOptions);
        
        // Get compression info
        const info = getCompressionInfo(file, processedFile);
        setCompressionInfo(info);

        setProfilePicture(processedFile);
        
        // Create preview
        const reader = new FileReader();
        reader.onload = (e) => {
          setProfilePicturePreview(e.target.result);
        };
        reader.readAsDataURL(processedFile);
        
        setUploadDialogOpen(false);

        // Show compression message if file was compressed
        if (info.wasCompressed) {
          setMessage({
            type: 'info',
            text: `Image compressed from ${info.originalSizeMB}MB to ${info.compressedSizeMB}MB (${info.compressionRatio}% reduction)`
          });
        }

      } catch (error) {
        console.error('Error processing image:', error);
        setMessage({
          type: 'error',
          text: `Failed to process image: ${error.message}`
        });
      } finally {
        setCompressing(false);
      }
    }
  };

  const handleSaveProfile = async () => {
    setLoading(true);
    try {
      const token = await getToken();
      const formData = new FormData();
      
      // Add name if changed
      if (editedName !== (user?.name || auth0User?.name)) {
        formData.append('name', editedName.trim());
      }
      
      // Add profile picture if selected
      if (profilePicture) {
        formData.append('profilePicture', profilePicture);
      }

      // Make API call to update profile
      const response = await axios.put(`${config.apiUrl}/api/users/profile`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        setMessage({
          type: 'success',
          text: 'Profile updated successfully!'
        });
        setEditMode(false);
        setProfilePicture(null);
        
        // Update the auth context if needed
        // This might require updating the AuthContext to refresh user data
      } else {
        throw new Error(response.data.message || 'Failed to update profile');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to update profile. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveProfilePicture = () => {
    setProfilePicture(null);
    setProfilePicturePreview(null);
    setUploadDialogOpen(false);
  };

  const clearMessage = () => {
    setMessage(null);
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, lg: 3 }, width: '100%' }}>
      <Typography variant="h4" sx={{ mb: 4 }}>User Profile</Typography>
      
      {message && (
        <Alert 
          severity={message.type} 
          sx={{ mb: 3 }}
          onClose={clearMessage}
        >
          {message.text}
        </Alert>
      )}
      
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 3 }}>
              <Box sx={{ position: 'relative', mb: 2 }}>
                <Avatar
                  src={profilePicturePreview}
                  alt={editedName || user?.name || auth0User?.name}
                  sx={{ width: 120, height: 120, mb: 1 }}
                >
                  {(editedName || user?.name || auth0User?.name || 'U').charAt(0).toUpperCase()}
                </Avatar>
                {editMode && (
                  <IconButton
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      right: 0,
                      bgcolor: 'primary.main',
                      color: 'white',
                      '&:hover': { bgcolor: 'primary.dark' }
                    }}
                    onClick={() => setUploadDialogOpen(true)}
                  >
                    <PhotoCameraIcon />
                  </IconButton>
                )}
              </Box>
              
              {editMode ? (
                <TextField
                  value={editedName}
                  onChange={handleNameChange}
                  variant="outlined"
                  size="small"
                  sx={{ mb: 2, textAlign: 'center' }}
                  inputProps={{
                    style: { textAlign: 'center' },
                    maxLength: 100
                  }}
                  placeholder="Enter your name"
                />
              ) : (
                <Typography variant="h5" sx={{ mb: 1, textAlign: 'center' }}>
                  {user?.name || auth0User?.name || 'No name set'}
                </Typography>
              )}
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {user?.email || auth0User?.email}
              </Typography>
              
              <Chip
                label={`Role: ${role}`}
                color="primary"
                variant="outlined"
                sx={{ mb: 2 }}
              />
              
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
                {editMode ? (
                  <>
                    <Button
                      variant="contained"
                      startIcon={loading ? <CircularProgress size={16} /> : <SaveIcon />}
                      onClick={handleSaveProfile}
                      disabled={loading}
                      size="small"
                    >
                      {loading ? 'Saving...' : 'Save'}
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<CancelIcon />}
                      onClick={handleEditToggle}
                      disabled={loading}
                      size="small"
                    >
                      Cancel
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={handleEditToggle}
                    size="small"
                  >
                    Edit Profile
                  </Button>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 3 }}>Account Information</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Name"
                    variant="outlined"
                    value={editMode ? editedName : (user?.name || auth0User?.name || '')}
                    disabled={!editMode}
                    onChange={editMode ? handleNameChange : undefined}
                    margin="normal"
                    inputProps={{ maxLength: 100 }}
                    helperText={editMode ? `${editedName.length}/100 characters` : ''}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    variant="outlined"
                    value={user?.email || auth0User?.email || ''}
                    disabled
                    margin="normal"
                    helperText="Email cannot be changed"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Role"
                    variant="outlined"
                    value={role || ''}
                    disabled
                    margin="normal"
                    helperText="Role is managed by administrators"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="User ID"
                    variant="outlined"
                    value={user?.id || auth0User?.sub || 'Not available'}
                    disabled
                    margin="normal"
                    helperText="Unique user identifier"
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />
              
              <Typography variant="h6" sx={{ mb: 2 }}>Account Status</Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Chip
                  label="Account Active"
                  color="success"
                  variant="outlined"
                />
                <Chip
                  label={`Last Login: ${new Date().toLocaleDateString()}`}
                  color="info"
                  variant="outlined"
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Profile Picture Upload Dialog */}
      <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)}>
        <DialogTitle>Update Profile Picture</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Choose a new profile picture. Supported formats: JPEG, PNG, GIF. Maximum size: 5MB.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
            {profilePicturePreview && (
              <Avatar
                src={profilePicturePreview}
                sx={{ width: 100, height: 100 }}
              />
            )}
            <Button
              component="label"
              variant="outlined"
              startIcon={compressing ? <CircularProgress size={16} /> : <PhotoCameraIcon />}
              disabled={compressing}
            >
              {compressing ? 'Processing...' : 'Choose Image'}
              <input
                type="file"
                hidden
                accept="image/jpeg,image/jpg,image/png,image/gif"
                onChange={handleProfilePictureChange}
              />
            </Button>
            {compressionInfo && compressionInfo.wasCompressed && (
              <Typography variant="caption" color="primary" sx={{ textAlign: 'center' }}>
                Image compressed by {compressionInfo.compressionRatio}% 
                ({compressionInfo.originalSizeMB}MB → {compressionInfo.compressedSizeMB}MB)
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)}>Cancel</Button>
          {profilePicturePreview && (
            <Button onClick={handleRemoveProfilePicture} color="error">
              Remove Picture
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Profile; 